import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Search, ExternalLink, Receipt, FileText, RotateCcw, Calendar, Trash2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n, c = "RON") => `${(Number(n) || 0).toFixed(2)} ${c}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString("ro-RO") : "—");

// Sub-tab „Comenzi” — facturile emise pentru comenzi (comportamentul existent).
function OrdersInvoicesTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/invoices`, {
        params: { page, per_page: perPage, search: search || undefined, type },
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, type]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invoices/stats`);
      setStats(r.data);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  return (
    <>
      {stats && (
        <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Facturi emise</div>
            <div className="mt-1 text-xl font-semibold text-zinc-100">{stats.invoices_total}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Storno-uri</div>
            <div className="mt-1 text-xl font-semibold text-rose-300">{stats.stornos_total}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Anulate (cancel)</div>
            <div className="mt-1 text-xl font-semibold text-amber-300">{stats.cancelled_total}</div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input data-testid="inv-search" placeholder="Caută nr factură, comandă sau client..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-950 pl-9 pr-3 py-2 text-sm text-zinc-100" />
        </div>
        <select data-testid="inv-type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
          <option value="all">Toate</option>
          <option value="factura">Doar facturi</option>
          <option value="storno">Doar storno</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Tip</th>
              <th className="px-3 py-2 text-left">Nr factură</th>
              <th className="px-3 py-2 text-left">Comandă</th>
              <th className="px-3 py-2 text-left">Client</th>
              <th className="px-3 py-2 text-right">Valoare</th>
              <th className="px-3 py-2 text-left">Emisă</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-zinc-500">Se încarcă...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-xs text-zinc-500">
                <Receipt className="mx-auto mb-2 h-6 w-6 text-zinc-700" />
                Nicio factură emisă încă.
              </td></tr>
            )}
            {!loading && items.map((it, idx) => (
              <tr key={`${it.order_id}-${it.type}-${idx}`} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-3 py-2">
                  {it.type === "storno" ? (
                    <span className="inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-medium text-rose-200">
                      <RotateCcw className="h-3 w-3" /> Storno
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-200">
                      <FileText className="h-3 w-3" /> Factură
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-medium text-zinc-100">
                  {it.number}
                  {it.reference && (
                    <div className="text-[10px] text-zinc-500">referință: {it.reference}</div>
                  )}
                  {it.cancelled_at && (
                    <div className="text-[10px] text-amber-400">anulată: {fmtDate(it.cancelled_at)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-zinc-300">
                  #{it.order_id}
                  <div className="text-[10px] text-zinc-500">{it.integration_key}</div>
                </td>
                <td className="px-3 py-2 text-zinc-300">
                  <div className="truncate max-w-[200px]" title={it.customer_name}>{it.customer_name || "—"}</div>
                  {it.customer_email && <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">{it.customer_email}</div>}
                </td>
                <td className="px-3 py-2 text-right text-zinc-200">{fmt(it.amount, it.currency)}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{fmtDate(it.issued_at)}</td>
                <td className="px-3 py-2 text-right">
                  {it.url ? (
                    <a href={it.url} target="_blank" rel="noreferrer" data-testid={`inv-open-${it.order_id}-${it.type}`}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
                      <ExternalLink className="h-3 w-3" /> PDF
                    </a>
                  ) : <span className="text-[10px] text-zinc-600">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > perPage && (
        <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
          <div>Afișez {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} din {total}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="rounded border border-zinc-800 px-2 py-1 disabled:opacity-40">‹ Înapoi</button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * perPage >= total}
              className="rounded border border-zinc-800 px-2 py-1 disabled:opacity-40">Înainte ›</button>
          </div>
        </div>
      )}
    </>
  );
}

// Sub-tab „Lunare" — PDF-uri de facturi salvate din bulk-import-pdf,
// grupate pe luna, cu download selectiv sau download luna intreaga (zip).
const ROMANIAN_MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];
// 3 min — ZIP cu zeci de facturi se construiește lent (fiecare PDF e citit
// din GridFS / Mongo + comprimat). Default-ul global de 25s (vezi AuthContext)
// crapă chiar și pentru 20 de facturi, de aici user raport 2026-10-05.
const DOWNLOAD_TIMEOUT_MS = 180000;

function MonthlyInvoicesTab() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(currentYear);
  const [monthNum, setMonthNum] = useState(now.getMonth() + 1); // 1..12
  const month = `${year}-${String(monthNum).padStart(2, "0")}`;
  const [country, setCountry] = useState(""); // "" | "RO" | "BG"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = { month };
      if (country) params.country = country;
      const r = await axios.get(`${API}/financiar/monthly-invoices`, { params });
      setItems(r.data?.items || []);
    } catch (_e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [month, country]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a);
    a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const downloadOne = async (it) => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/financiar/monthly-invoices/${it.id}/download`,
        { responseType: "blob", timeout: DOWNLOAD_TIMEOUT_MS },
      );
      triggerBlobDownload(r.data, it.original_filename || `factura-${it.id}.pdf`);
    } catch (e) {
      window.alert(`Download eșuat: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const downloadSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/financiar/monthly-invoices/download-bulk`,
        { ids: Array.from(selected) },
        { responseType: "blob", timeout: DOWNLOAD_TIMEOUT_MS },
      );
      triggerBlobDownload(r.data, `facturi-selectate-${selected.size}.zip`);
    } catch (e) {
      window.alert(`Download eșuat: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Șterge ${selected.size} factură/facturi lunare?\n(soft-delete — pot fi restaurate din DB)`)) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/financiar/monthly-invoices/delete-bulk`,
        { ids: Array.from(selected) },
      );
      window.alert(`Șterse: ${r.data?.deleted || 0} / ${r.data?.requested || 0}`);
      setSelected(new Set());
      await fetchData();
    } catch (e) {
      window.alert(`Ștergere eșuată: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const runDedupe = async () => {
    if (!window.confirm("Scanează Facturi Lunare pentru duplicate (aceeași țară + lună + număr factură + furnizor)?\n\nPăstrează varianta cea mai veche și șterge (soft) restul.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/financiar/monthly-invoices/dedupe`, {});
      const d = r.data || {};
      window.alert(`Duplicate găsite: ${d.duplicate_groups} grupuri (${d.duplicates_found} facturi extra).\nȘterse: ${d.deleted}.`);
      await fetchData();
    } catch (e) {
      window.alert(`Scanare eșuată: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const downloadMonth = async () => {
    setBusy(true);
    try {
      const body = { month };
      if (country) body.country = country;
      const r = await axios.post(
        `${API}/financiar/monthly-invoices/download-bulk`,
        body,
        { responseType: "blob", timeout: DOWNLOAD_TIMEOUT_MS },
      );
      triggerBlobDownload(r.data, `facturi-lunare-${month}.zip`);
    } catch (e) {
      window.alert(`Download eșuat: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        {/* Month picker custom — HTML5 `type=month` nu are suport uniform
            (Safari macOS/iOS îl afișează doar ca text input). Două selects
            native sunt accesibile peste tot și clienții nu mai scriu manual. */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-zinc-500" />
          <select
            data-testid="inv-monthly-month-select"
            value={monthNum}
            onChange={(e) => setMonthNum(Number(e.target.value))}
            className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
          >
            {ROMANIAN_MONTHS.map((label, idx) => (
              <option key={idx + 1} value={idx + 1}>{label}</option>
            ))}
          </select>
          <select
            data-testid="inv-monthly-year-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
          >
            {Array.from({ length: 7 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <select
          data-testid="inv-monthly-country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
        >
          <option value="">Toate țările</option>
          <option value="RO">RO</option>
          <option value="BG">BG</option>
        </select>
        <div className="flex-1" />
        <button
          data-testid="inv-monthly-dl-selected"
          onClick={downloadSelected}
          disabled={busy || selected.size === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FileText className="h-3.5 w-3.5" /> Download selectate ({selected.size})
        </button>
        <button
          data-testid="inv-monthly-delete-selected"
          onClick={deleteSelected}
          disabled={busy || selected.size === 0}
          title="Șterge (soft-delete) facturile selectate"
          className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-200 hover:bg-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 className="h-3.5 w-3.5" /> Șterge selectate ({selected.size})
        </button>
        <button
          data-testid="inv-monthly-dedupe"
          onClick={runDedupe}
          disabled={busy}
          title="Scanează Facturi Lunare și șterge duplicatele automate (aceeași țară + lună + număr factură + furnizor)"
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-40"
        >
          <ShieldCheck className="h-3.5 w-3.5" /> Verifică duplicate
        </button>
        <button
          data-testid="inv-monthly-dl-month"
          onClick={downloadMonth}
          disabled={busy || items.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Calendar className="h-3.5 w-3.5" /> Download luna ({items.length})
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="w-10 px-3 py-2 text-left">
                <input
                  type="checkbox"
                  data-testid="inv-monthly-check-all"
                  checked={items.length > 0 && selected.size === items.length}
                  onChange={toggleAll}
                  className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-950"
                />
              </th>
              <th className="px-3 py-2 text-left">Fișier</th>
              <th className="px-3 py-2 text-left">Furnizor</th>
              <th className="px-3 py-2 text-left">Nr. factură</th>
              <th className="px-3 py-2 text-left">Țară</th>
              <th className="px-3 py-2 text-right">Valoare</th>
              <th className="px-3 py-2 text-left">Uploaded</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-xs text-zinc-500">Se încarcă...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-xs text-zinc-500">
                <Calendar className="mx-auto mb-2 h-6 w-6 text-zinc-700" />
                Nicio factură lunară pentru {month}{country ? ` · ${country}` : ""}.
                <div className="mt-1 text-[10px] text-zinc-600">Upload facturi bulk din Financiar → se salvează automat aici.</div>
              </td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    data-testid={`inv-monthly-check-${it.id}`}
                    checked={selected.has(it.id)}
                    onChange={() => toggleOne(it.id)}
                    className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-950"
                  />
                </td>
                <td className="px-3 py-2 font-medium text-zinc-100 truncate max-w-[280px]" title={it.original_filename}>
                  {it.original_filename}
                </td>
                <td className="px-3 py-2 text-zinc-300 truncate max-w-[180px]" title={it.supplier_name}>
                  {it.supplier_name || "—"}
                </td>
                <td className="px-3 py-2 text-zinc-300 font-mono text-xs">{it.invoice_number || "—"}</td>
                <td className="px-3 py-2 text-zinc-400 text-xs">{it.country}</td>
                <td className="px-3 py-2 text-right text-zinc-200">{fmt(it.total, it.currency)}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{fmtDate(it.uploaded_at)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid={`inv-monthly-dl-${it.id}`}
                    onClick={() => downloadOne(it)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                  >
                    <ExternalLink className="h-3 w-3" /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function Invoices() {
  const [subTab, setSubTab] = useState("orders"); // "orders" | "monthly"

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="invoices-page">
      <div className="mb-5">
        <div className="text-[11px] text-zinc-500">Panou de control / <span className="text-zinc-300">Facturi</span></div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-50">Facturi emise</h1>
        <p className="text-sm text-zinc-500">Toate facturile și storno-urile emise prin softul nostru.</p>
      </div>

      {/* Sub-tabs */}
      <div className="mb-5 inline-flex rounded-lg border border-zinc-800 bg-zinc-950/60 p-1">
        <button
          data-testid="inv-subtab-orders"
          onClick={() => setSubTab("orders")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
            subTab === "orders"
              ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/40"
              : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <Receipt className="h-3.5 w-3.5" /> Comenzi
        </button>
        <button
          data-testid="inv-subtab-monthly"
          onClick={() => setSubTab("monthly")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
            subTab === "monthly"
              ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/40"
              : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <Calendar className="h-3.5 w-3.5" /> Lunare
        </button>
      </div>

      {subTab === "orders" && <OrdersInvoicesTab />}
      {subTab === "monthly" && <MonthlyInvoicesTab />}
    </div>
  );
}
